import {
  Modal,
  Input,
  Button,
  Tag,
  Spin,
  message as Message,
  Dropdown,
  Menu,
  Space,
  Popconfirm,
  Divider,
} from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import axiosInstance from "../config/axiosInstance";
import { webAPIs } from "../config/webAPIs";

export default function AssignAttributeModal({
  open,
  onClose,
  projectId,
  assigningDocId = null, // optional: if provided, enables tag click for assignment
  handleAssignLevel = () => {}, // optional: callback(attrId, levelId)
}) {
  const [attrLoading, setAttrLoading] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [newAttributeName, setNewAttributeName] = useState("");

  // State for inline editing of attributes
  const [editingAttributeId, setEditingAttributeId] = useState(null);
  const [editingAttributeName, setEditingAttributeName] = useState("");

  // State for inline editing of levels
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [editingLevelValue, setEditingLevelValue] = useState("");

  // State for adding a new level to a specific attribute
  const [addingLevelToAttrId, setAddingLevelToAttrId] = useState(null);
  const [newLevelValue, setNewLevelValue] = useState("");

  // --- Data Fetching ---
  const fetchAttributes = async () => {
    if (!projectId) return;
    setAttrLoading(true);
    try {
      const res = await axiosInstance.get(
        `${webAPIs.listAttributes}?projectId=${projectId}`
      );
      setAttributes(res.data || []);
    } catch {
      Message.error("Failed to fetch attributes.");
      setAttributes([]);
    } finally {
      setAttrLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAttributes();
    } else {
      // Reset all state on close to ensure a clean slate
      setNewAttributeName("");
      setEditingAttributeId(null);
      setEditingLevelId(null);
      setAddingLevelToAttrId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  // --- State Management for UI Actions ---
  const startEditAttribute = (attr) => {
    setEditingAttributeId(attr.id);
    setEditingAttributeName(attr.name);
  };
  const cancelEditAttribute = () => {
    setEditingAttributeId(null);
    setEditingAttributeName("");
  };

  const startEditLevel = (level) => {
    setEditingLevelId(level.id);
    setEditingLevelValue(level.value);
  };
  const cancelEditLevel = () => {
    setEditingLevelId(null);
    setEditingLevelValue("");
  };

  const startAddLevel = (attrId) => {
    setAddingLevelToAttrId(attrId);
    setNewLevelValue("");
  };
  const cancelAddLevel = () => {
    setAddingLevelToAttrId(null);
    setNewLevelValue("");
  };

  // --- API Handlers ---
  const handleCreateAttribute = async () => {
    const name = newAttributeName.trim();
    if (!name) return;
    setAttrLoading(true);
    try {
      await axiosInstance.post(webAPIs.createAttribute, { projectId, name });
      setNewAttributeName("");
      await fetchAttributes();
      Message.success("Attribute created!");
    } catch {
      Message.error("Failed to create attribute");
    } finally {
      setAttrLoading(false);
    }
  };

  const handleUpdateAttribute = async () => {
    const name = editingAttributeName.trim();
    if (!name || !editingAttributeId) return;
    setAttrLoading(true);
    try {
      await axiosInstance.put(webAPIs.updateAttribute(editingAttributeId), { name });
      cancelEditAttribute();
      await fetchAttributes();
      Message.success("Attribute updated!");
    } catch {
      Message.error("Failed to update attribute");
    } finally {
      setAttrLoading(false);
    }
  };

  const handleDeleteAttribute = async (id) => {
    setAttrLoading(true);
    try {
      await axiosInstance.delete(webAPIs.deleteAttribute(id));
      await fetchAttributes();
      Message.success("Attribute deleted!");
    } catch {
      Message.error("Failed to delete attribute");
    } finally {
      setAttrLoading(false);
    }
  };

  const handleAddLevel = async () => {
    const value = newLevelValue.trim();
    if (!value || !addingLevelToAttrId) return;
    setAttrLoading(true);
    try {
      await axiosInstance.post(webAPIs.createLevel, {
        attributeId: addingLevelToAttrId,
        value,
      });
      cancelAddLevel();
      await fetchAttributes();
      Message.success("Level added!");
    } catch {
      Message.error("Failed to add level");
    } finally {
      setAttrLoading(false);
    }
  };

  const handleUpdateLevel = async () => {
    const value = editingLevelValue.trim();
    if (!value || !editingLevelId) return;
    setAttrLoading(true);
    try {
      await axiosInstance.put(webAPIs.updateLevel(editingLevelId), { value });
      cancelEditLevel();
      await fetchAttributes();
      Message.success("Level updated!");
    } catch {
      Message.error("Failed to update level");
    } finally {
      setAttrLoading(false);
    }
  };

  const handleDeleteLevel = async (id) => {
    setAttrLoading(true);
    try {
      await axiosInstance.delete(webAPIs.deleteLevel(id));
      await fetchAttributes();
      Message.success("Level deleted!");
    } catch {
      Message.error("Failed to delete level");
    } finally {
      setAttrLoading(false);
    }
  };

  // --- Render Methods for Menus ---
  const attributeMenu = (attr) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => startEditAttribute(attr)}>
        Edit Name
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
        <Popconfirm
          title="Delete this attribute?"
          description="This will delete the attribute and all its levels. This action cannot be undone."
          onConfirm={() => handleDeleteAttribute(attr.id)}
          okText="Yes, Delete"
          cancelText="No"
        >
          Delete Attribute
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Assign Document Attributes"
      width={600}
      destroyOnClose
    >
      <Spin spinning={attrLoading}>
        <div style={{ marginBottom: 24, marginTop: 16 }}>
          <Space.Compact style={{ width: "100%", gap: 8}}>
            <Input
              placeholder="Create a New Attribute Name"
              value={newAttributeName}
              onChange={(e) => setNewAttributeName(e.target.value)}
              onPressEnter={handleCreateAttribute}
            />
            <Button
              className="bg-[#007bff] text-white flex items-center"
              icon={<PlusOutlined />}
              onClick={handleCreateAttribute}
              disabled={!newAttributeName.trim()}
            >
              Add
            </Button>
          </Space.Compact>
        </div>

        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
          {attributes.map((attr) => (
            <div
              key={attr.id}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: 16,
              }}
            >
              {/* Attribute Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                {editingAttributeId === attr.id ? (
                  <Space.Compact>
                    <Input
                      value={editingAttributeName}
                      onChange={(e) => setEditingAttributeName(e.target.value)}
                      onPressEnter={handleUpdateAttribute}
                    />
                    <Button className="bg-[#007bff] text-white" onClick={handleUpdateAttribute}>Save</Button>
                    <Button onClick={cancelEditAttribute}>Cancel</Button>
                  </Space.Compact>
                ) : (
                  <h3 style={{ margin: 0, fontWeight: 600, fontSize: '16px' }}>
                    {attr.name}
                  </h3>
                )}
                <Dropdown overlay={attributeMenu(attr)} trigger={["click"]}>
                  <Button type="text" icon={<MoreOutlined />} shape="circle" />
                </Dropdown>
              </div>

              {/* Levels Container */}
              <Space size={[8, 12]} wrap>
                {attr.DocumentAttributeLevels?.map((level) =>
                  editingLevelId === level.id ? (
                    <Space.Compact key={level.id}>
                      <Input
                        size="small"
                        value={editingLevelValue}
                        onChange={(e) => setEditingLevelValue(e.target.value)}
                        onPressEnter={handleUpdateLevel}
                        style={{ width: 100 }}
                        autoFocus
                      />
                      <Button size="small" className="bg-[#007bff] text-white" onClick={handleUpdateLevel}>Save</Button>
                      <Button size="small" onClick={cancelEditLevel}>Cancel</Button>
                    </Space.Compact>
                  ) : (
                    <Tag
                      key={level.id}
                      closable
                      onClose={(e) => {
                          e.preventDefault();
                      }}
                      closeIcon={
                        <Popconfirm
                            title="Delete this level?"
                            onConfirm={() => handleDeleteLevel(level.id)}
                            okText="Delete"
                            cancelText="Cancel"
                        >
                            <DeleteOutlined style={{fontSize: '10px'}}/>
                        </Popconfirm>
                      }
                      color={assigningDocId ? "blue" : "default"}
                      onClick={() => assigningDocId && handleAssignLevel(attr.id, level.id)}
                      onDoubleClick={() => startEditLevel(level)}
                      style={{
                        cursor: assigningDocId ? "pointer" : "default",
                        padding: "4px 8px",
                      }}
                    >
                      {level.value} <EditOutlined style={{fontSize: '10px', marginLeft: '4px', color: '#888'}}/>
                    </Tag>
                  )
                )}

                {/* Add New Level UI */}
                {addingLevelToAttrId === attr.id ? (
                  <Space.Compact>
                    <Input
                      size="small"
                      placeholder="New Level"
                      value={newLevelValue}
                      onChange={(e) => setNewLevelValue(e.target.value)}
                      onPressEnter={handleAddLevel}
                      style={{ width: 100 }}
                      autoFocus
                    />
                    <Button size="small" type="primary" onClick={handleAddLevel}>Add</Button>
                    <Button size="small" onClick={cancelAddLevel}>Cancel</Button>
                  </Space.Compact>
                ) : (
                  <Tag
                    onClick={() => startAddLevel(attr.id)}
                    style={{
                      background: "#fff",
                      borderStyle: "dashed",
                      cursor: "pointer",
                    }}
                  >
                    <PlusOutlined /> New Level
                  </Tag>
                )}
              </Space>
            </div>
          ))}
        </div>
      </Spin>
    </Modal>
  );
}